/**
 * Pure helpers for the Import wizard tree/filter/heuristic logic. No React, no
 * DOM — unit-tested in importHelpers.test.js. The tree shape comes from
 * utils/importTree.js (`buildImportTree`): folders have
 * { name, path, tags[], metadata{}, folders:Map, files:[] } and files have
 * { name, file, tags[], metadata{}, include }.
 */

import { extensionOf } from "../../utils/format";

// ── Extension → category (asset-pack oriented) ──────────────────────────────
const CATEGORY_BY_EXT = {
    wav: "Audio", mp3: "Audio", ogg: "Audio", flac: "Audio", aiff: "Audio", aif: "Audio", m4a: "Audio",
    png: "Image", jpg: "Image", jpeg: "Image", gif: "Image", tga: "Image", bmp: "Image",
    tiff: "Image", tif: "Image", webp: "Image", exr: "Image", psd: "Image", svg: "Image", hdr: "Image",
    fbx: "3D Model", obj: "3D Model", gltf: "3D Model", glb: "3D Model", blend: "3D Model",
    dae: "3D Model", stl: "3D Model", "3ds": "3D Model", ply: "3D Model", usdz: "3D Model",
    mp4: "Video", mov: "Video", webm: "Video", avi: "Video", mkv: "Video",
    pdf: "Document", txt: "Document", md: "Document", doc: "Document", docx: "Document",
    json: "Document", xml: "Document", csv: "Document", rtf: "Document",
    zip: "Archive", rar: "Archive", "7z": "Archive", tar: "Archive", gz: "Archive",
};

// `extensionOf` lives in utils/format.js now (shared with the zip importer);
// re-exported so existing importers of this module keep working.
export { extensionOf };

export function categoryOf(name) {
    return CATEGORY_BY_EXT[extensionOf(name)] || "Other";
}

/** Case-insensitive union of tag names, preserving order + original casing. */
export function unionNames(a, b) {
    const seen = new Set();
    const out = [];
    for (const raw of [...(a || []), ...(b || [])]) {
        const value = String(raw).trim().replace(/\s+/g, " ");
        const key = value.toLowerCase();
        if (!value || seen.has(key)) continue;
        seen.add(key);
        out.push(value);
    }
    return out;
}

/** { category -> file count } across the whole tree (for the filter bar). */
export function categoryCounts(root) {
    const counts = {};
    const walk = (node) => {
        for (const f of node.files) {
            const c = categoryOf(f.name);
            counts[c] = (counts[c] || 0) + 1;
        }
        for (const child of node.folders.values()) walk(child);
    };
    if (root) walk(root);
    return counts;
}

/**
 * Does a file pass the current filters? `filters`:
 *   { categories:Set<string>, name:string, minBytes:number, maxBytes:number,
 *     excludeDuplicates:boolean }
 * An empty/absent `categories` set means "no category restriction".
 */
export function fileMatchesFilters(fileNode, filters) {
    if (!filters) return true;
    const size = (fileNode.file && fileNode.file.size) || 0;

    if (filters.categories && filters.categories.size > 0 && !filters.categories.has(categoryOf(fileNode.name))) {
        return false;
    }
    if (filters.name && !fileNode.name.toLowerCase().includes(filters.name.toLowerCase())) {
        return false;
    }
    if (filters.minBytes > 0 && size < filters.minBytes) return false;
    if (filters.maxBytes > 0 && size > filters.maxBytes) return false;
    if (filters.excludeDuplicates && fileNode._duplicate) return false;
    return true;
}

/** Set each file's `include` flag from the filters (mutates the tree). */
export function applyFiltersToTree(root, filters) {
    const walk = (node) => {
        for (const f of node.files) f.include = fileMatchesFilters(f, filters);
        for (const child of node.folders.values()) walk(child);
    };
    if (root) walk(root);
}

/** 'all' | 'some' | 'none' — included state across a folder's descendant files. */
export function folderIncludeState(node) {
    let total = 0;
    let included = 0;
    const walk = (n) => {
        for (const f of n.files) {
            total++;
            if (f.include !== false) included++;
        }
        for (const child of n.folders.values()) walk(child);
    };
    walk(node);
    if (total === 0 || included === 0) return "none";
    return included === total ? "all" : "some";
}

/** Set `include` on every descendant file of a subtree (mutates). */
export function setIncludeForSubtree(node, value) {
    const walk = (n) => {
        for (const f of n.files) f.include = value;
        for (const child of n.folders.values()) walk(child);
    };
    walk(node);
}

/** Total bytes of the currently-included files (for the review summary). */
export function includedBytes(root) {
    let bytes = 0;
    const walk = (node) => {
        for (const f of node.files) {
            if (f.include !== false) bytes += (f.file && f.file.size) || 0;
        }
        for (const child of node.folders.values()) walk(child);
    };
    if (root) walk(root);
    return bytes;
}

/**
 * Heuristic auto-tag: give every (non-root) folder a tag equal to its own name
 * (cleaned), unless it already has it. This realizes the user's example —
 * folder "SFX" → tag "SFX", subfolder "Metal" → tag "Metal" — which then
 * inherits down recursively at flatten time. Returns the number of tags added.
 */
export function tagFoldersByName(root) {
    let added = 0;
    const clean = (name) => String(name || "").replace(/[_-]+/g, " ").trim().replace(/\s+/g, " ");
    const walk = (node, isRoot) => {
        if (!isRoot) {
            const name = clean(node.name);
            if (name) {
                const next = unionNames(node.tags, [name]);
                if (next.length !== node.tags.length) {
                    node.tags = next;
                    added++;
                }
            }
        }
        for (const child of node.folders.values()) walk(child, false);
    };
    if (root) walk(root, true);
    return added;
}

/**
 * Build per-folder descriptors for the AI auto-tagger: one entry per non-empty
 * (non-root) folder with the TEXT signal the model uses (names + a type
 * histogram). No file contents — files aren't uploaded yet at this step. `key`
 * is the folder path (used to route suggestions back).
 */
export function buildFolderDescriptors(root) {
    const out = [];
    const walk = (node, isRoot) => {
        if (!isRoot && (node.files.length > 0 || node.folders.size > 0)) {
            const fileTypeHistogram = {};
            for (const f of node.files) {
                const ext = extensionOf(f.name) || "none";
                fileTypeHistogram[ext] = (fileTypeHistogram[ext] || 0) + 1;
            }
            out.push({
                key: node.path,
                name: node.name,
                path: node.path,
                childFolderNames: [...node.folders.values()].map((c) => c.name),
                sampleFileNames: node.files.slice(0, 25).map((f) => f.name),
                fileTypeHistogram,
            });
        }
        for (const child of node.folders.values()) walk(child, false);
    };
    if (root) walk(root, true);
    return out;
}

/**
 * Attach AI suggestions (keyed by folder path) onto the tree as transient
 * `node.suggestedTags` (only tags the folder doesn't already have) and
 * `node.aiDescription`. Returns how many fresh suggested tags were attached.
 */
export function applySuggestions(root, suggestions) {
    const byPath = {};
    (suggestions || []).forEach((s) => {
        byPath[s.key] = s;
    });
    let count = 0;
    const walk = (node) => {
        const s = byPath[node.path];
        if (s) {
            const own = new Set((node.tags || []).map((t) => t.toLowerCase()));
            const fresh = (s.tags || []).filter((t) => !own.has(String(t).toLowerCase()));
            node.suggestedTags = fresh;
            if (s.description) node.aiDescription = s.description;
            count += fresh.length;
        }
        node.folders.forEach(walk);
    };
    if (root) walk(root);
    return count;
}

/** Accept every pending suggestion across the tree (suggested → real tags). */
export function acceptAllSuggestions(root) {
    let count = 0;
    const walk = (node) => {
        if (node.suggestedTags && node.suggestedTags.length > 0) {
            node.tags = unionNames(node.tags, node.suggestedTags);
            count += node.suggestedTags.length;
            node.suggestedTags = [];
        }
        node.folders.forEach(walk);
    };
    if (root) walk(root);
    return count;
}

/** Count pending (un-accepted) AI suggestions across the tree. */
export function countSuggestions(root) {
    let count = 0;
    const walk = (node) => {
        count += (node.suggestedTags || []).length;
        node.folders.forEach(walk);
    };
    if (root) walk(root);
    return count;
}

/**
 * Serialize just the TAGGING decisions (by path — never file bytes) so they can
 * be persisted for crash-safety and re-applied after the user re-selects the
 * same folder. Only nodes the user actually touched are stored, keeping the
 * snapshot small.
 */
export function serializeTagging(root) {
    const folders = {};
    const files = {};
    const walk = (node, isRoot) => {
        if (!isRoot) {
            const touched =
                (node.tags || []).length ||
                Object.keys(node.metadata || {}).length ||
                (node.suggestedTags || []).length ||
                node.aiDescription;
            if (touched) {
                folders[node.path] = {
                    tags: node.tags || [],
                    metadata: node.metadata || {},
                    suggestedTags: node.suggestedTags || [],
                    aiDescription: node.aiDescription || "",
                };
            }
        }
        for (const f of node.files) {
            const path = node.path ? `${node.path}/${f.name}` : f.name;
            const touched = (f.tags || []).length || Object.keys(f.metadata || {}).length || f.include === false;
            if (touched) files[path] = { tags: f.tags || [], metadata: f.metadata || {}, include: f.include };
        }
        for (const child of node.folders.values()) walk(child, false);
    };
    if (root) walk(root, true);
    return { folders, files };
}

/** Re-apply a serialized tagging snapshot onto a freshly-built tree, matching by
 *  path. Returns how many nodes were restored. */
export function applyTagging(root, snapshot) {
    if (!root || !snapshot) return 0;
    const folders = snapshot.folders || {};
    const files = snapshot.files || {};
    let count = 0;
    const walk = (node, isRoot) => {
        if (!isRoot && folders[node.path]) {
            const s = folders[node.path];
            node.tags = s.tags || [];
            node.metadata = s.metadata || {};
            if ((s.suggestedTags || []).length) node.suggestedTags = s.suggestedTags;
            if (s.aiDescription) node.aiDescription = s.aiDescription;
            count++;
        }
        for (const f of node.files) {
            const path = node.path ? `${node.path}/${f.name}` : f.name;
            if (files[path]) {
                const s = files[path];
                f.tags = s.tags || [];
                f.metadata = s.metadata || {};
                if (s.include === false) f.include = false;
                count++;
            }
        }
        for (const child of node.folders.values()) walk(child, false);
    };
    walk(root, true);
    return count;
}

/**
 * Flatten the tree into the ORDERED, visible rows for the tree view, respecting
 * an `expanded` Set of folder paths. Each row carries the tags it INHERITS from
 * its ancestors so the UI can show inherited vs own chips without re-walking.
 *
 * Row: { key, type:'folder'|'file', depth, node, path, parentPath, name,
 *        inheritedTags:string[], hasChildren?, open? }
 */
export function flattenVisibleRows(root, expanded) {
    const rows = [];
    const exp = expanded || new Set();

    const walkChildren = (node, depth, inheritedForNode) => {
        // Tags this node passes down to its children + its own files inherit.
        const passedDown = unionNames(inheritedForNode, node.tags);

        const folders = [...node.folders.values()].sort((a, b) => a.name.localeCompare(b.name));
        const files = [...node.files].sort((a, b) => a.name.localeCompare(b.name));

        for (const folder of folders) {
            const open = exp.has(folder.path);
            rows.push({
                key: "d:" + folder.path,
                type: "folder",
                depth,
                node: folder,
                path: folder.path,
                name: folder.name,
                inheritedTags: passedDown,
                hasChildren: folder.folders.size > 0 || folder.files.length > 0,
                open,
            });
            if (open) walkChildren(folder, depth + 1, passedDown);
        }

        for (const file of files) {
            rows.push({
                key: "f:" + node.path + "/" + file.name,
                type: "file",
                depth,
                node: file,
                path: node.path,
                parentPath: node.path,
                name: file.name,
                inheritedTags: passedDown,
            });
        }
    };

    if (root) walkChildren(root, 0, []);
    return rows;
}
